import { useQuery } from "@apollo/client";
import {
  BaseTable,
  Button,
  Flexbox,
  IBaseTableProps,
  Modal
} from "@sede-x/shell-ds-react-framework";
import { ColumnDef, getPaginationRowModel } from "@tanstack/react-table";
import calculateSize from "calculate-size";
import dayjs from "dayjs";
import { loader } from "graphql.macro";
import { useCallback, useMemo, useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import { GqlResponse, TTicketRevision } from "ticketing/ticketing.types";
import { formatDateForDisplay } from "ticketing/utils";
import LoadingPanel from "../../../shared/components/LoadingPanel";
import RevisionsTicketMovements from "./RevisionsTicketMovements";
import RevisionsTicketRailcars from "./RevisionsTicketRailcars";
import RevisionsTicketVolumes from "./RevisionsTicketVolumes";
type Response = GqlResponse<TTicketRevision[], "ticketRevisions">;

const query = loader("../../ticketing-graphql/ticketRevisions.graphql");

const PADDING = 40;
const FONT_OPTIONS = {
  font: "14px Segoe UI",
  fontWeight: "normal"
};
const minWidth = 80;
const maxAllowedWidth = 400;

// Helper for getting nested property values
const propertyGetter = (path: string) => {
  return <T extends TTicketRevisionTransformed>(obj: T): unknown => {
    return path.split(".").reduce((prev: unknown, curr: string) => {
      return prev && typeof prev === "object" ? (prev as Record<string, unknown>)[curr] : null;
    }, obj as unknown);
  };
};

type TTicketRevisionTransformed = Omit<TTicketRevision, "lastModifiedBy"> & {
  lastModifiedBy?: string;
};

const transformData = (ticketRevisions: TTicketRevision[]) => {
  return ticketRevisions
    ?.map(ticket => ({
      ...ticket,
      lastModifiedDate: new Date(ticket?.lastModifiedDate + "Z"),
      startDate: new Date(ticket?.startDate + "Z"),
      lastModifiedBy: [ticket.lastModifiedBy.lastName, ticket.lastModifiedBy.firstName]
        .filter(Boolean)
        .join(", ")
    }))
    .sort((t1, t2) => t2.lastModifiedDate.getTime() - t1.lastModifiedDate.getTime());
};

const renderActionButton = (
  revisionNumber: string,
  handler: (rev: string) => void,
  label: string
) => {
  return (
    <Button
      size="small"
      style={{ borderRadius: "5px" }}
      onClick={() => handler(revisionNumber)}>
      {label}
    </Button>
  );
};

/**
 *
 * @param param0
 * @returns
 */
const RevisionsTicket = ({
  ticketId,
  handleCancel
}: {
  ticketId: string;
  handleCancel: () => void;
}) => {
  const [revisions, setRevisions] = useState<TTicketRevisionTransformed[]>();
  const { loading, error } = useQuery<Response>(query, {
    fetchPolicy: "no-cache",
    variables: { id: ticketId },
    onCompleted: data => setRevisions(transformData(data.ticketRevisions))
  });

  const [revisionNumber, setRevisionNumber] = useState<string>();
  const [showMovements, setShowMovements] = useState<boolean>(false);
  const [showVolumes, setShowVolumes] = useState<boolean>(false);
  const [showRailcars, setShowRailcars] = useState<boolean>(false);

  const handleTicketMovementRevisions = (revNumber: string) => {
    setRevisionNumber(revNumber);
    setShowMovements(true);
  };
  const handleTicketVolumeRevisions = (revNumber: string) => {
    setRevisionNumber(revNumber);
    setShowVolumes(true);
  };
  const handleTicketsRailcarsRevisions = (revNumber: string) => {
    setRevisionNumber(revNumber);
    setShowRailcars(true);
  };
  const hideMovementRevisions = () => setShowMovements(false);
  const hideVolumeRevisions = () => setShowVolumes(false);
  const hideRailcars = () => setShowRailcars(false);

  const calculateWidth = useCallback(
    (field: string, title: string, format?: string) => {
      let maxWidth = calculateSize(title, FONT_OPTIONS).width + PADDING;

      if (revisions && field) {
        revisions.forEach(item => {
          // Get the value using our property getter
          const getValue = propertyGetter(field);
          const value = getValue(item);

          const formattedValue =
            value instanceof Date && format ? formatDateForDisplay(value) : String(value);

          const contentWidth = calculateSize(formattedValue, FONT_OPTIONS).width + PADDING;
          if (contentWidth > maxWidth) {
            maxWidth = contentWidth;
          }
        });
      }
      const finalWidth = maxWidth + 10;

      return Math.min(Math.max(finalWidth, minWidth), maxAllowedWidth);
    },
    [revisions]
  );

  const columns = useMemo<ColumnDef<TTicketRevisionTransformed, string>[]>(
    () => [
      {
        header: "Ticket ID",
        accessorKey: "ticketNumber",
        size: calculateWidth("ticketNumber", "Ticket ID")
      },
      {
        header: "Revision #",
        accessorKey: "revisionNumber",
        size: calculateWidth("revisionNumber", "Revision #")
      },
      {
        header: "Ticket Date",
        accessorKey: "startDate",
        cell: ({ row }) => dayjs(row.original.startDate).format("MM/DD/YY"),
        size: calculateWidth("startDate", "Ticket Date", "MM/DD/YY")
      },
      {
        header: "Update Date",
        accessorKey: "lastModifiedDate",
        cell: ({ row }) => dayjs(row.original.lastModifiedDate).format("MM/DD/YY HH:mm:ss"),
        size: calculateWidth("lastModifiedDate", "Update Date", "MM/DD/YY HH:mm:ss")
      },
      {
        header: "Updated By",
        accessorKey: "lastModifiedBy",
        size: calculateWidth("lastModifiedBy", "Updated By")
      },
      {
        header: "Batch",
        accessorKey: "batch.name",
        size: calculateWidth("batch.name", "Batch")
      },
      {
        header: "Ship From",
        accessorKey: "shipFromCode.description",
        accessorFn: row => row.shipFromCode?.description ?? "",
        size: calculateWidth("shipFromCode.description", "Ship From")
      },
      {
        header: "Ship To",
        accessorKey: "shipToCode.description",
        accessorFn: row => row.shipToCode?.description ?? "",
        size: calculateWidth("shipToCode.description", "Ship To")
      },
      {
        header: "Product",
        accessorKey: "product.name",
        size: calculateWidth("product.name", "Product")
      },
      {
        header: "Carrier",
        accessorKey: "carrier.name",
        accessorFn: row => row.carrier?.name ?? "",
        size: calculateWidth("carrier.name", "Carrier")
      },
      {
        header: "Border Crossing Date",
        accessorKey: "borderCrossingDate",
        cell: ({ row }) =>
          row.original.borderCrossingDate
            ? dayjs(row.original.borderCrossingDate).format("MM/DD/YY")
            : "",
        size: calculateWidth("borderCrossingDate", "Border Crossing Date", "MM/DD/YY")
      },
      {
        header: "Comments",
        accessorKey: "invoiceComment",
        size: calculateWidth("invoiceComment", "Comments")
      },
      {
        header: "PO Number",
        accessorKey: "poNumber",
        size: calculateWidth("poNumber", "PO Number")
      },
      {
        header: "Movements",
        accessorKey: "movementsAction",
        cell: props =>
          renderActionButton(
            props.row.original.revisionNumber,
            handleTicketMovementRevisions,
            "Movements"
          ),
        size: calculateWidth("movementsAction", "Movements")
      },
      {
        header: "Volumes",
        accessorKey: "volumesAction",
        cell: props =>
          renderActionButton(
            props.row.original.revisionNumber,
            handleTicketVolumeRevisions,
            "Volumes"
          ),
        size: calculateWidth("volumesAction", "Volumes")
      },
      {
        header: "Railcars",
        accessorKey: "railcarsAction",
        cell: props =>
          renderActionButton(
            props.row.original.revisionNumber,
            handleTicketsRailcarsRevisions,
            "Railcars"
          ),
        size: calculateWidth("railcarsAction", "Railcars")
      }
    ],
    [calculateWidth]
  );

  const tableOptions: IBaseTableProps<TTicketRevisionTransformed>["tableOptions"] = {
    defaultColumn: {
      minSize: 100,
      maxSize: 400
    },
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    getPaginationRowModel: getPaginationRowModel()
  };

  return (
    <>
      <Modal
        open={true}
        title={`Ticket Revisions (System ID: ${ticketId})`}
        height={300}
        onClose={handleCancel}
        width={1080}
        bodyPadding={false}
        maskClosable={false}>
        {loading && <LoadingPanel />}
        {error && <ApolloErrorViewer error={error} />}
        <Flexbox
          flexDirection="column"
          style={{
            overflow: "auto"
          }}>
          <BaseTable
            columns={columns}
            data={revisions}
            style={{ width: "100%", tableLayout: "fixed" }}
            tableOptions={tableOptions}
            noFooter={true}
          />
        </Flexbox>
      </Modal>

      {showMovements && revisionNumber && (
        <Modal
          bodyPadding={false}
          open={true}
          title={"Ticket Revision MovementIds"}
          onClose={hideMovementRevisions}>
          <RevisionsTicketMovements
            ticketId={ticketId}
            revisionNumber={revisionNumber}></RevisionsTicketMovements>
        </Modal>
      )}
      {showVolumes && revisionNumber && (
        <Modal
          bodyPadding={false}
          open={true}
          width={1080}
          title={"Ticket Revision Volumes"}
          onClose={hideVolumeRevisions}>
          <RevisionsTicketVolumes
            ticketId={ticketId}
            revisionNumber={revisionNumber}></RevisionsTicketVolumes>
        </Modal>
      )}
      {showRailcars && revisionNumber && (
        <Modal
          bodyPadding={false}
          open={true}
          title={"Ticket Revision Railcars"}
          onClose={hideRailcars}>
          <RevisionsTicketRailcars
            ticketId={ticketId}
            revisionNumber={revisionNumber}></RevisionsTicketRailcars>
        </Modal>
      )}
    </>
  );
};

export default RevisionsTicket;
